import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { LEAD_SOURCES, CALL_STATUSES, ORDER_STATUSES, LANGUAGES, ITEMS, PAYMENT_MODES, getStatusBadgeClass } from '../lib/constants';
import { Plus, Search, Edit2, Trash2, X, Phone, ChevronDown } from 'lucide-react';

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterSource, setFilterSource] = useState('All');
  const [filterApt, setFilterApt] = useState('All');

  const [form, setForm] = useState({
    name: '', mobile: '', date: '', contacted: '', area: '', apt: '', flat: '', floor: '',
    source: '', sourceOther: '', callStatus: '', orderStatus: 'New', followup: '',
    langs: [], interested: [], paid: 0, paymode: '', bought: [], remarks: ''
  });

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'All') params.status = filterStatus;
      if (filterSource !== 'All') params.source = filterSource;
      if (filterApt !== 'All') params.apt = filterApt;
      if (search) params.search = search;
      const [leadsRes, aptsRes] = await Promise.all([api.getLeads(params), api.getApartments()]);
      setLeads(leadsRes.data);
      setApartments(aptsRes.data);
    } catch (err) { console.error('Fetch error:', err.message); }
    setLoading(false);
  }, [filterStatus, filterSource, filterApt, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const resetForm = () => setForm({
    name: '', mobile: '', date: new Date().toISOString().split('T')[0], contacted: '', area: '', apt: '', flat: '', floor: '',
    source: '', sourceOther: '', callStatus: '', orderStatus: 'New', followup: '',
    langs: [], interested: [], paid: 0, paymode: '', bought: [], remarks: ''
  });

  const openNew = () => { resetForm(); setEditLead(null); setShowModal(true); };
  const openEdit = (lead) => {
    setForm({ ...lead, paid: lead.paid || 0, langs: lead.langs || [], interested: lead.interested || [], bought: lead.bought || [] });
    setEditLead(lead);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editLead) {
        await api.updateLead(editLead.id, form);
      } else {
        await api.createLead(form);
      }
      setShowModal(false);
      fetchLeads();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await api.deleteLead(id);
    fetchLeads();
  };

  const toggleArray = (field, val) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(val) ? f[field].filter(v => v !== val) : [...f[field], val]
    }));
  };

  const getOrderStatusLabel = (s) => {
    if (!s) return 'New';
    if (s.toLowerCase().includes('callback')) return 'Callback';
    if (s.toLowerCase().includes('lost')) return 'Lost';
    return s;
  };

  const SelectField = ({ label, value, onChange, options, testId }) => (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>{label}</label>
      <div className="relative">
        <select data-testid={testId} value={value} onChange={onChange} className="w-full px-3 py-2.5 rounded-lg text-sm appearance-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }}>
          {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
      </div>
    </div>
  );

  return (
    <div data-testid="leads-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Leads</h2>
          <p className="text-sm mt-1" style={{ color: '#A8A29E' }}>{leads.length} leads in pipeline</p>
        </div>
        <button data-testid="add-lead-button" onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
          <Plus className="w-4 h-4" /> Add Lead
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#78716C' }} />
          <input data-testid="lead-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or mobile..." className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }} />
        </div>
        <div className="relative">
          <select data-testid="lead-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2.5 rounded-lg text-sm appearance-none pr-8" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
            {['All', 'New', 'Interested', 'Callback', 'Converted', 'Lost'].map(s => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
        </div>
        <div className="relative">
          <select data-testid="lead-filter-source" value={filterSource} onChange={e => setFilterSource(e.target.value)} className="px-3 py-2.5 rounded-lg text-sm appearance-none pr-8" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
            <option>All</option>
            {LEAD_SOURCES.map(s => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
        </div>
        <div className="relative">
          <select data-testid="lead-filter-apt" value={filterApt} onChange={e => setFilterApt(e.target.value)} className="px-3 py-2.5 rounded-lg text-sm appearance-none pr-8" style={{ background: '#1E1A17', border: '1px solid #2A241F', color: '#F5F5F4' }}>
            <option>All</option>
            {apartments.map(a => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#78716C' }} />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>
        <div className="overflow-x-auto">
          <table className="w-full gm-table">
            <thead>
              <tr><th>Name</th><th>Mobile</th><th>Apartment</th><th>Source</th><th>Call Status</th><th>Order Status</th><th>Follow-up</th><th>Paid</th><th></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8" style={{ color: '#78716C' }}>Loading...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8" style={{ color: '#78716C' }}>No leads found. Click "Add Lead" to get started.</td></tr>
              ) : leads.map((lead, i) => (
                <tr key={lead.id} className="fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                  <td style={{ color: '#F5F5F4' }} className="font-medium">{lead.name}</td>
                  <td><a href={`tel:${lead.mobile}`} className="inline-flex items-center gap-1" style={{ color: '#C8A96E' }}><Phone className="w-3 h-3" />{lead.mobile}</a></td>
                  <td>{lead.apt}{lead.flat ? ` / ${lead.flat}` : ''}</td>
                  <td className="text-xs">{lead.source}</td>
                  <td className="text-xs">{lead.callStatus}</td>
                  <td><span className={`${getStatusBadgeClass(getOrderStatusLabel(lead.orderStatus))} px-2.5 py-0.5 rounded-full text-xs font-semibold`}>{lead.orderStatus}</span></td>
                  <td className="text-xs">{lead.followup}</td>
                  <td>{lead.paid > 0 && <span style={{ color: '#4ADE80' }}>{lead.paid}</span>}</td>
                  <td>
                    <div className="flex gap-1">
                      <button data-testid={`edit-lead-${lead.id}`} onClick={() => openEdit(lead)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#A8A29E' }} onMouseEnter={e => e.currentTarget.style.color = '#C8A96E'} onMouseLeave={e => e.currentTarget.style.color = '#A8A29E'}><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-lead-${lead.id}`} onClick={() => handleDelete(lead.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: '#A8A29E' }} onMouseEnter={e => e.currentTarget.style.color = '#F87171'} onMouseLeave={e => e.currentTarget.style.color = '#A8A29E'}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-2xl rounded-xl max-h-[85vh] overflow-y-auto" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.3)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #2A241F' }}>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>{editLead ? 'Edit Lead' : 'Add New Lead'}</h3>
              <button data-testid="close-lead-modal" onClick={() => setShowModal(false)} className="p-1.5 rounded-lg" style={{ color: '#A8A29E' }}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Full Name *</label>
                  <input data-testid="lead-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Mobile *</label>
                  <input data-testid="lead-mobile" required maxLength={10} value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Lead Date</label>
                  <input data-testid="lead-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Contacted Date</label>
                  <input type="date" value={form.contacted} onChange={e => setForm({...form, contacted: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                </div>
                <SelectField label="Apartment" value={form.apt} onChange={e => setForm({...form, apt: e.target.value})} options={['', ...apartments.map(a => a.name)]} testId="lead-apt" />
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Flat No.</label>
                  <input value={form.flat} onChange={e => setForm({...form, flat: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Floor</label>
                  <input value={form.floor} onChange={e => setForm({...form, floor: e.target.value})} placeholder="e.g. 3" className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Area</label>
                  <input value={form.area} onChange={e => setForm({...form, area: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <SelectField label="Lead Source *" value={form.source} onChange={e => setForm({...form, source: e.target.value})} options={['', ...LEAD_SOURCES]} testId="lead-source" />
                {form.source === 'Other' && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Specify Source</label>
                    <input value={form.sourceOther} onChange={e => setForm({...form, sourceOther: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                  </div>
                )}
                <SelectField label="Call Status" value={form.callStatus} onChange={e => setForm({...form, callStatus: e.target.value})} options={['', ...CALL_STATUSES]} testId="lead-callstatus" />
                <SelectField label="Order Status" value={form.orderStatus} onChange={e => setForm({...form, orderStatus: e.target.value})} options={ORDER_STATUSES} testId="lead-orderstatus" />
                {form.orderStatus?.includes('Callback') && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Follow-up Date</label>
                    <input type="date" value={form.followup} onChange={e => setForm({...form, followup: e.target.value})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4', colorScheme: 'dark' }} />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Paid Amount</label>
                  <input type="number" value={form.paid} onChange={e => setForm({...form, paid: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
                </div>
                <SelectField label="Payment Mode" value={form.paymode} onChange={e => setForm({...form, paymode: e.target.value})} options={['', ...PAYMENT_MODES]} testId="lead-paymode" />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#C8A96E' }}>Languages Spoken</label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map(l => (
                    <button key={l} type="button" onClick={() => toggleArray('langs', l)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{ background: form.langs.includes(l) ? 'rgba(200,169,110,0.2)' : '#100E0C', border: `1px solid ${form.langs.includes(l) ? 'rgba(200,169,110,0.5)' : '#2A241F'}`, color: form.langs.includes(l) ? '#C8A96E' : '#A8A29E' }}
                    >{l}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#C8A96E' }}>Interested In</label>
                <div className="flex flex-wrap gap-2">
                  {[...ITEMS, 'Monthly Subscription'].map(item => (
                    <button key={item} type="button" onClick={() => toggleArray('interested', item)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{ background: form.interested.includes(item) ? 'rgba(200,169,110,0.2)' : '#100E0C', border: `1px solid ${form.interested.includes(item) ? 'rgba(200,169,110,0.5)' : '#2A241F'}`, color: form.interested.includes(item) ? '#C8A96E' : '#A8A29E' }}
                    >{item}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#C8A96E' }}>Remarks</label>
                <textarea data-testid="lead-remarks" value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={3} className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' }} />
              </div>

              <div className="flex gap-3 pt-2">
                <button data-testid="save-lead-button" type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ background: '#C8A96E', color: '#000' }}>
                  {editLead ? 'Update Lead' : 'Save Lead'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all" style={{ background: '#2A241F', color: '#A8A29E' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
